// Brand color palettes — override the app-wide #1E2A9C primary and #29B7E3 accent
// via CSS custom properties. Saved in localStorage.

export const PALETTES = [
  {
    id: "navy",
    label: "Navy Ocean",
    tagline: "Klasik · Kurumsal",
    primary: "#1E2A9C",
    primaryHover: "#121A68",
    accent: "#29B7E3",
    tint: "#EEF2FF",
  },
  {
    id: "emerald",
    label: "Emerald Forest",
    tagline: "Doğal · Güven",
    primary: "#059669",
    primaryHover: "#047857",
    accent: "#34D399",
    tint: "#ECFDF5",
  },
  {
    id: "violet",
    label: "Violet Dream",
    tagline: "Lüks · Yaratıcı",
    primary: "#7C3AED",
    primaryHover: "#5B21B6",
    accent: "#A78BFA",
    tint: "#F5F3FF",
  },
  {
    id: "amber",
    label: "Amber Gold",
    tagline: "Sıcak · Prestij",
    primary: "#D97706",
    primaryHover: "#B45309",
    accent: "#FCD34D",
    tint: "#FFFBEB",
  },
  {
    id: "rose",
    label: "Rose Blossom",
    tagline: "Cesur · Modern",
    primary: "#E11D48",
    primaryHover: "#9F1239",
    accent: "#FB7185",
    tint: "#FFF1F2",
  },
  {
    id: "graphite",
    label: "Graphite Ink",
    tagline: "Minimalist · Şık",
    primary: "#0F172A",
    primaryHover: "#020617",
    accent: "#64748B",
    tint: "#F1F5F9",
  },
];

const KEY = "madymark_brand";

export function getBrand() {
  return localStorage.getItem(KEY) || "navy";
}

export function applyBrand(id, customPalette) {
  const p = customPalette || PALETTES.find(x => x.id === id) || PALETTES[0];
  const s = document.documentElement.style;
  s.setProperty("--brand-primary", p.primary);
  s.setProperty("--brand-primary-hover", p.primaryHover);
  s.setProperty("--brand-accent", p.accent);
  s.setProperty("--brand-tint", p.tint);
  localStorage.setItem(KEY, p.id);
  if (customPalette) localStorage.setItem("madymark_brand_custom", JSON.stringify(customPalette));
  return p;
}

export function getCustomPalette() {
  try {
    const raw = localStorage.getItem("madymark_brand_custom");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function bootBrand() {
  const id = getBrand();
  if (id === "custom") {
    const p = getCustomPalette();
    if (p) applyBrand("custom", p);
    else applyBrand("navy");
  } else applyBrand(id);
}

// ---- Extract dominant colors from an image URL (logo) → palette
function _hexOf(r, g, b) {
  return "#" + [r, g, b].map(v => v.toString(16).padStart(2, "0").toUpperCase()).join("");
}

function _hslOf(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  let h = 0, s = 0; const l = (mx + mn) / 2;
  if (mx !== mn) {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    switch (mx) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
      case g: h = ((b - r) / d + 2); break;
      case b: h = ((r - g) / d + 4); break;
    }
    h *= 60;
  }
  return { h, s, l };
}

function _rgbFromHsl(h, s, l) {
  h /= 360;
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function _shiftLightness(hex, targetL) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const { h, s } = _hslOf(r, g, b);
  const [nr, ng, nb] = _rgbFromHsl(h, s, targetL);
  return _hexOf(nr, ng, nb);
}

export async function extractLogoPalette(imageUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const size = 80;
      const canvas = document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, size, size);
      let data;
      try { data = ctx.getImageData(0, 0, size, size).data; }
      catch (e) { reject(new Error("CORS: logo indirilemiyor")); return; }
      const bucket = new Map();
      const QUANT = 24; // merge similar colors
      for (let i = 0; i < data.length; i += 4) {
        const a = data[i + 3]; if (a < 200) continue;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        // Skip pure white/near-white and near-black backgrounds
        if (r > 240 && g > 240 && b > 240) continue;
        if (r < 15 && g < 15 && b < 15) continue;
        const { s } = _hslOf(r, g, b);
        // Prefer saturated colors — weight low-saturation entries less
        const key = `${Math.round(r/QUANT)}-${Math.round(g/QUANT)}-${Math.round(b/QUANT)}`;
        const weight = 1 + s * 3;
        const prev = bucket.get(key);
        if (prev) { prev.count += weight; prev.r += r; prev.g += g; prev.b += b; prev.n += 1; }
        else bucket.set(key, { count: weight, r, g, b, n: 1 });
      }
      const clusters = Array.from(bucket.values())
        .map(c => ({ hex: _hexOf(Math.round(c.r/c.n), Math.round(c.g/c.n), Math.round(c.b/c.n)),
                     r: c.r/c.n, g: c.g/c.n, b: c.b/c.n, count: c.count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
      if (clusters.length === 0) { reject(new Error("Renk bulunamadı — logo tek renkli veya şeffaf")); return; }

      // Primary: the top color, darkened toward L=0.35 for legibility on white
      const first = clusters[0];
      const { h: h1, s: s1 } = _hslOf(first.r, first.g, first.b);
      const [pr, pg, pb] = _rgbFromHsl(h1, Math.min(1, Math.max(0.4, s1)), 0.35);
      const primary = _hexOf(pr, pg, pb);
      const primaryHover = _shiftLightness(primary, 0.22);

      // Accent: pick 2nd distinct hue if available; else complement
      let accent;
      const second = clusters.find(c => {
        const { h: h2 } = _hslOf(c.r, c.g, c.b);
        return Math.abs(h2 - h1) > 30;
      });
      if (second) {
        const { h: h2, s: s2 } = _hslOf(second.r, second.g, second.b);
        const [ar, ag, ab] = _rgbFromHsl(h2, Math.min(1, Math.max(0.5, s2)), 0.55);
        accent = _hexOf(ar, ag, ab);
      } else {
        // Analogous hue +40°
        const [ar, ag, ab] = _rgbFromHsl((h1 + 40) % 360, Math.min(1, s1), 0.55);
        accent = _hexOf(ar, ag, ab);
      }
      const tint = _shiftLightness(primary, 0.95);
      resolve({
        id: "custom",
        label: "Logo Paleti",
        tagline: "Logonuzdan otomatik türetildi",
        primary, primaryHover, accent, tint,
        _source: clusters.map(c => c.hex),
      });
    };
    img.onerror = () => reject(new Error("Logo yüklenemedi"));
    img.src = imageUrl + (imageUrl.includes("?") ? "&" : "?") + "cachebust=" + Date.now();
  });
}

